import type { JobFinancialSummaryDto } from '../types';

interface JobFinancialSummaryProps {
  financial: JobFinancialSummaryDto;
}

export function JobFinancialSummary({ financial }: JobFinancialSummaryProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent.toFixed(2)}%`;
  };

  return (
    <div className="bg-white p-1.5 rounded-[20px] border border-[#d9d9d9]">
      <h3 className="text-[#0A0A9E] font-semibold text-sm mb-1">Financial Summary</h3>

      <div className="grid grid-cols-3 gap-3">
        {/* Column 1 */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Total Charges:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalCharges)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Total Costs:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalCosts)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Total Tax:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalTax)}</span>
          </div>
        </div>

        {/* Column 2 */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Payments Received:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalPaymentsReceived)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Payments Pending:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.totalPaymentsPending)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Customer Balance:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.customerBalance)}</span>
          </div>
        </div>

        {/* Column 3 */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Profit:</span>
            <span className={`text-sm font-semibold ${financial.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(financial.profit)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Profit %:</span>
            <span className={`text-sm font-semibold ${financial.profitPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercent(financial.profitPercent)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#84878A]">Job Balance:</span>
            <span className="text-sm font-semibold">{formatCurrency(financial.jobBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
